import { useEffect, useRef, useState } from "react";

const defaultData = [
  { label: "Jan", value: 20 },
  { label: "Feb", value: 35 },
  { label: "Mar", value: 28 },
  { label: "Apr", value: 52 },
  { label: "May", value: 45 },
  { label: "Jun", value: 60 },
  { label: "Jul", value: 55 },
  { label: "Aug", value: 78 },
  { label: "Sep", value: 72 },
  { label: "Oct", value: 85 },
  { label: "Nov", value: 90 },
  { label: "Dec", value: 100 },
];

const ScrollChart = ({
  data = defaultData,
  width = 800,
  height = 200,
  padding = { top: 40, right: 40, bottom: 60, left: 60 },
  lineColor = "#2563eb",
  areaColor = "rgba(37, 99, 235, 0.08)",
  gridColor = "#e5e7eb",
  labelColor = "#6b7280",
  strokeWidth = 2.5,
  showArea = true,
  showDots = true,
  showGrid = true,
  showLabels = true,
  triggerOffset = 0.15,
  className = "",
}) => {
  const containerRef = useRef(null);
  const [progress, setProgress] = useState(0);

  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const maxValue = Math.max(...data.map((d) => d.value));
  const minValue = Math.min(...data.map((d) => d.value));
  const valueRange = maxValue - minValue || 1;

  // Build points
  const points = data.map((d, i) => ({
    x: padding.left + (i / (data.length - 1)) * chartW,
    y: padding.top + chartH - ((d.value - minValue) / valueRange) * chartH,
    label: d.label,
    value: d.value,
  }));

  // Build SVG path
  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");

  // Area path (closed shape)
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${padding.top + chartH} L ${points[0].x} ${padding.top + chartH} Z`;

  // Calculate total path length for stroke-dasharray animation
  const pathRef = useRef(null);
  const [pathLength, setPathLength] = useState(0);

  useEffect(() => {
    if (pathRef.current) {
      setPathLength(pathRef.current.getTotalLength());
    }
  }, [data]);

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const windowH = window.innerHeight;

      const start = windowH * (1 - triggerOffset);
      const end = windowH * triggerOffset;

      if (rect.top >= start) {
        setProgress(0);
      } else if (rect.top <= end) {
        setProgress(1);
      } else {
        setProgress((start - rect.top) / (start - end));
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [triggerOffset]);

  // Grid lines
  const gridLines = 5;
  const gridValues = Array.from(
    { length: gridLines },
    (_, i) => minValue + (valueRange / (gridLines - 1)) * i,
  );

  // How many dots to show based on progress
  const visibleDots = Math.floor(progress * points.length);

  // Clipped area width based on progress
  const clipX = padding.left + progress * chartW;

  return (
    <div ref={containerRef} className={`w-full ${className}`}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          {/* Clip path for area fill */}
          <clipPath id="area-clip">
            <rect
              x={padding.left}
              y={padding.top}
              width={Math.max(0, clipX - padding.left)}
              height={chartH}
            />
          </clipPath>

          {/* Gradient for area */}
          <linearGradient id="area-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lineColor} stopOpacity="0.15" />
            <stop offset="100%" stopColor={lineColor} stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {showGrid &&
          gridValues.map((val, i) => {
            const y =
              padding.top + chartH - ((val - minValue) / valueRange) * chartH;
            return (
              <g key={`grid-${i}`}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + chartW}
                  y2={y}
                  stroke={gridColor}
                  strokeWidth={1}
                  strokeDasharray="4 4"
                />
                {showLabels && (
                  <text
                    x={padding.left - 12}
                    y={y + 4}
                    textAnchor="end"
                    fill={labelColor}
                    fontSize={12}
                    fontFamily="sans-serif"
                  >
                    {Math.round(val)}
                  </text>
                )}
              </g>
            );
          })}

        {/* X-axis labels */}
        {showLabels &&
          points.map((p, i) => (
            <text
              key={`label-${i}`}
              x={p.x}
              y={padding.top + chartH + 24}
              textAnchor="middle"
              fill={labelColor}
              fontSize={12}
              fontFamily="sans-serif"
              opacity={i <= visibleDots ? 1 : 0.2}
              style={{ transition: "opacity 0.3s ease" }}
            >
              {p.label}
            </text>
          ))}

        {/* Area fill */}
        {showArea && (
          <path
            d={areaPath}
            fill="url(#area-gradient)"
            clipPath="url(#area-clip)"
          />
        )}

        {/* Animated line */}
        <path
          ref={pathRef}
          d={linePath}
          fill="none"
          stroke={lineColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={pathLength}
          strokeDashoffset={pathLength - progress * pathLength}
          style={{ transition: "stroke-dashoffset 0.05s linear" }}
        />

        {/* Dots */}
        {showDots &&
          points.map((p, i) => (
            <g key={`dot-${i}`}>
              {/* Outer glow */}
              <circle
                cx={p.x}
                cy={p.y}
                r={6}
                fill={lineColor}
                opacity={i < visibleDots ? 0.15 : 0}
                style={{ transition: "opacity 0.3s ease" }}
              />
              {/* Inner dot */}
              <circle
                cx={p.x}
                cy={p.y}
                r={3}
                fill="white"
                stroke={lineColor}
                strokeWidth={2}
                opacity={i < visibleDots ? 1 : 0}
                style={{ transition: "opacity 0.3s ease" }}
              />
            </g>
          ))}

        {/* Leading dot (animated at the tip) */}
        {progress > 0 &&
          progress < 1 &&
          pathRef.current &&
          (() => {
            const point = pathRef.current.getPointAtLength(
              progress * pathLength,
            );
            return (
              <circle
                cx={point.x}
                cy={point.y}
                r={5}
                fill={lineColor}
                opacity={0.8}
              >
                <animate
                  attributeName="r"
                  values="4;7;4"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.8;0.3;0.8"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </circle>
            );
          })()}
      </svg>
    </div>
  );
};

export default ScrollChart;
